<template>
  <div class="analyze-params-selector tool-bar">
    <div class="form-container">
      <!-- 検索期間 -->
      <b-row no-gutters class="form-section">
        <b-row no-gutters class="target-label-wrapper">
          <label class="label" v-t="'appAnalyzeParamsSelector.searchPeriod'" />
          <span v-if="datetimeState === false" class="target-caption error">
            {{ $tc('appAnalyzeParamsSelector.message.hoursOver', maxHours) }}
          </span>
          <span v-else class="target-caption">
            {{ `(${$tc('appAnalyzeParamsSelector.hoursUpTo', maxHours)})` }}
          </span>
        </b-row>
        <b-form-radio-group
          class="search-period-options"
          v-model="activePeriod"
          v-bind:options="periods"
          buttons
          inline
          button-variant="light"
          size="sm"
          v-on:change="$_onPeriodChanged"
        />
        <datetime-selector
          period-flg
          time-flg
          append-fifty-nine
          v-bind:period-string="'〜'"
          v-bind:startDate="fromDate"
          v-bind:startTime="fromTime"
          v-bind:endDate="toDate"
          v-bind:endTime="toTime"
          v-bind:state="datetimeState"
          v-bind:endMax="toMax"
          v-on:datetime-value-change="$_onDatetimeChanged"
          v-on:datetime-selectend="$_onDatetimeChanged"
        />
      </b-row>
      <!-- 対象ユーザー -->
      <b-row no-gutters class="form-section">
        <b-row no-gutters class="target-label-wrapper">
          <label class="label" v-t="'appAnalyzeParamsSelector.targetUsers'" />
          <span class="target-caption">
            {{
              `(${$tc('appAnalyzeParamsSelector.usersUpTo', maxUsersCount)})`
            }}
          </span>
        </b-row>
        <b-row no-gutters class="target-users-selector-wrapper">
          <input-keyword-user
            v-bind:idPrefix="idPrefix"
            v-bind:placeholder="
              $t('appAnalyzeParamsSelector.placeholder.keyword')
            "
            v-bind:value="keyword"
            v-bind:keywordTargets="keywordTargets"
            v-on:keyword-changed="$_onKeywordChanged"
            v-on:keyword-target-changed="$_onKeywordTargetChanged"
          />
          <b-button
            size="sm"
            variant="success"
            class="user-list-button"
            v-t="'appAnalyzeParamsSelector.action.selectUsers'"
            v-on:click="$_showUserSelector"
          />
        </b-row>
      </b-row>
      <!-- 表示エリア -->
      <b-row
        no-gutters
        v-if="isIndoorAvailable && isUsersSet"
        class="form-section"
      >
        <label class="label" v-t="'appAnalyzeParamsSelector.targetArea'" />
        <analyze-indoor-building-selector
          v-bind:id-prefix="idPrefix"
          v-bind:users="users"
          v-bind:activeIndoorBuildingId="indoorBuildingId"
          v-on:indoor-building-selected="$_onIndoorBuildingSelected"
        />
      </b-row>
    </div>
    <!-- ユーザー選択 -->
    <user-selector
      ref="user-selector"
      v-bind:value="users"
      v-bind:max-count="maxUsersCount"
      v-bind:keyword-target="keywordTargets"
      v-bind:indoor-sites="$store.getters['indoorSite/indoorSites']"
      class="user-selector"
      v-on:change:value="$_emitUserChangedEvent"
      v-on:change:keywordTarget="
        $store.dispatch('userKeywordTarget/setKeywordTargets', $event)
      "
    />
  </div>
</template>

<script>
import moment from 'moment'
import { RestApiClient } from 'ifield-core-components'
import { mapState, mapGetters } from 'vuex'
import InputKeywordUser from '@/components/AppInputKeywordUser.vue'
import AnalyzeIndoorBuildingSelector from '@/components/analyze/AppAnalyzeIndoorBuildingSelector.vue'
import {
  FUNCTION_TYPE,
  DATETIME_FORMAT_API,
  DATE_FORMAT_HYPHEN,
  TIME_FORMAT_MINUTE,
  TIME_FORMAT_SECOND,
} from '@/constants.js'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

// "カスタム"期間の値
const PERIOD_VALUE_CUSTOM = 99

/**
 * 時刻をparseする時の有効フォーマット
 * @type {Array.<String>}
 */
const VALID_TIME_FORMATS = [
  TIME_FORMAT_MINUTE,
  TIME_FORMAT_SECOND,
  'HH:mm:ss.SSS',
]

/**
 * 分析パラメータ選択コンポーネント
 */
export default {
  name: 'AppAnalayzeParamsSelector',
  components: {
    'input-keyword-user': InputKeywordUser,
    'analyze-indoor-building-selector': AnalyzeIndoorBuildingSelector,
  },
  props: {
    /**
     * 開始日時
     */
    from: {
      type: String,
      default: null,
    },
    /**
     * 終了日時
     */
    to: {
      type: String,
      default: null,
    },
    /**
     * 対象ユーザー
     */
    users: {
      type: Array,
      default: null,
    },
    /**
     * 屋内ビル
     */
    indoorBuildings: {
      type: Array,
      default: null,
    },
    /**
     * 最大指定期間(時間)
     */
    maxHours: {
      type: Number,
      default: 24,
    },
    /**
     * 最大ユーザー数
     */
    maxUsersCount: {
      type: Number,
      default: 30,
    },
    /**
     * 期間のバリデーション状態
     */
    periodState: {
      type: Boolean,
      default: null,
    },
    /**
     * 期間のプリセット
     */
    periodPresets: {
      type: Array,
      default: null,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      // キーワード
      keyword: '',
      // 現在
      now: moment(),
      // アクティブ期間
      activePeriod: 1,
      // 期間選択の表示用バリデーション状態
      datetimeState: null,
      // idPrefix
      idPrefix: 'analyze-data',
    }
  },
  computed: {
    ...mapState('indoorBuilding', {
      /**
       * 全ビル情報
       * @type {Array.<Object>}
       */
      allIndoorBuildings: (state) => state.indoorBuildings,
    }),
    ...mapGetters('userKeywordTarget', ['keywordTargets']),
    /**
     * 屋内機能が利用可能か？
     * @type {Boolean}
     */
    isIndoorAvailable: function () {
      return !!this.$store.getters.functionType(FUNCTION_TYPE.INDOOR)
    },
    /**
     * 対象ユーザーが設定されている場合はtrue
     * @type {Boolean}
     */
    isUsersSet: function () {
      return this.users && this.users.length > 0
    },
    /**
     * 期間選択肢
     * @type {Array.<Object>}
     */
    periods: function () {
      const periods = Array.isArray(this.periodPresets)
        ? [...this.periodPresets]
        : [
            {
              value: 1,
              text: this.$t('appAnalyzeParamsSelector.period.today'),
              from: moment(this.now).startOf('day').format(DATETIME_FORMAT_API),
              to: moment(this.now).endOf('day').format(DATETIME_FORMAT_API),
            },
            {
              value: 2,
              text: this.$t('appAnalyzeParamsSelector.period.oneDayAgo'),
              from: moment(this.now)
                .subtract(1, 'days')
                .startOf('day')
                .format(DATETIME_FORMAT_API),
              to: moment(this.now)
                .subtract(1, 'days')
                .endOf('day')
                .format(DATETIME_FORMAT_API),
            },
            {
              value: 3,
              text: this.$t('appAnalyzeParamsSelector.period.twoDaysAgo'),
              from: moment(this.now)
                .subtract(2, 'days')
                .startOf('day')
                .format(DATETIME_FORMAT_API),
              to: moment(this.now)
                .subtract(2, 'days')
                .endOf('day')
                .format(DATETIME_FORMAT_API),
            },
            {
              value: 4,
              text: this.$t('appAnalyzeParamsSelector.period.oneWeekAgo'),
              from: moment(this.now)
                .subtract(7, 'days')
                .startOf('day')
                .format(DATETIME_FORMAT_API),
              to: moment(this.now)
                .subtract(7, 'days')
                .endOf('day')
                .format(DATETIME_FORMAT_API),
            },
          ]

      periods.push({
        value: PERIOD_VALUE_CUSTOM,
        text: this.$t('appAnalyzeParamsSelector.period.custom'),
        from: this.from,
        to: this.to,
      })

      return periods
    },
    /**
     * 開始日時のmoment
     * @type {Moment}
     */
    fromMoment: function () {
      if (!this.from) return null
      const m = moment(this.from)
      return m.isValid() ? m : null
    },
    /**
     * 終了日時のmoment
     * @type {Moment}
     */
    toMoment: function () {
      if (!this.to) return null
      const m = moment(this.to)
      return m.isValid() ? m : null
    },
    /**
     * 開始日付(AppDatetimeSelectorへのbinding用)
     * @type {String}
     */
    fromDate: function () {
      if (!this.fromMoment) return null
      return this.fromMoment.format(DATE_FORMAT_HYPHEN)
    },
    /**
     * 開始時刻(AppDatetimeSelectorへのbinding用なので分までしか持たない)
     * @type {String}
     */
    fromTime: function () {
      if (!this.fromMoment) return null
      return this.fromMoment.format(TIME_FORMAT_MINUTE)
    },
    /**
     * 終了日付(AppDatetimeSelectorへのbinding用)
     * @type {String}
     */
    toDate: function () {
      if (!this.toMoment) return null
      return this.toMoment.format(DATE_FORMAT_HYPHEN)
    },
    /**
     * 終了時刻(AppDatetimeSelectorへのbinding用なので分までしか持たない)
     * @type {String}
     */
    toTime: function () {
      if (!this.toMoment) return null
      return this.toMoment.format(TIME_FORMAT_MINUTE)
    },
    /**
     * 終了として選択できる最大日時
     * @type {String}
     */
    toMax: function () {
      if (!this.fromMoment) return null
      return moment(this.fromMoment)
        .add(this.maxHours, 'hours')
        .subtract(1, 'seconds')
        .format(`${DATE_FORMAT_HYPHEN}T${TIME_FORMAT_SECOND}`)
    },
    /**
     * 建物ID
     * @type {Number}
     */
    indoorBuildingId: function () {
      if (!this.indoorBuildings || !this.indoorBuildings.length) {
        return null
      }
      // 建物指定は単数なので先頭要素を返却
      return this.indoorBuildings[0].id
    },
  },
  created: function () {
    this.$_updateIndoorSite()
  },
  mounted: function () {
    this.$_initializePeriod()
    this.$_updateDatetimeState()
  },
  watch: {
    periodState: function () {
      this.$_updateDatetimeState()
    },
  },
  methods: {
    /**
     * 屋内サイト情報更新
     */
    $_updateIndoorSite: async function () {
      try {
        await this.$store.dispatch('indoorSite/updateIndoorSitesWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.warn('failed to fetch indoorSites. use fetched before.')
        })
      }
    },

    /**
     * 期間選択初期化
     */
    $_initializePeriod: function () {
      if (!this.from && !this.to) {
        this.$_onPeriodChanged(this.periods[0].value)
      } else {
        this.$_updateActivePeriod(this.from, this.to)
      }
    },

    /**
     * 表示用期間バリデーション状態更新
     */
    $_updateDatetimeState: function () {
      this.datetimeState = this.periodState === true ? null : this.periodState
    },

    /**
     * 期間選択変更時処理
     * @param {Number} value 変更値
     */
    $_onPeriodChanged: function (value) {
      if (value !== PERIOD_VALUE_CUSTOM) {
        const activePeriod = this.periods.find((p) => p.value === value)
        if (activePeriod.from !== this.from) {
          this.$_emitFromChangedEvent(activePeriod.from)
        }
        if (activePeriod.to !== this.to) {
          this.$_emitToChangedEvent(activePeriod.to)
        }
      }
    },

    /**
     * 日時選択変更時処理
     * @param {String} startDate 開始日付
     * @param {String} startTime 開始時刻
     * @param {String} endDate 終了日付
     * @param {String} endTime 終了時刻
     * @param {String} errorMessage エラーメッセージ
     */
    $_onDatetimeChanged: function (
      startDate,
      startTime,
      endDate,
      endTime,
      errorMessage
    ) {
      if (errorMessage) {
        this.$log.warn(errorMessage)
      }

      const from = this.$_formatDatetime(startDate, startTime || '00:00', 0)
      const to = this.$_formatDatetime(endDate, endTime || '00:00', 59)

      // 一致する期間プリセットがあったら変更する
      this.$_updateActivePeriod(from, to)

      if (from !== this.from) {
        this.$_emitFromChangedEvent(from)
      }
      if (to !== this.to) {
        this.$_emitToChangedEvent(to)
      }
    },

    /**
     * 日時をフォーマットする
     * @param {String} date 日付
     * @param {String} time 時刻
     * @param {Number} overwriteSecond 上書きする秒
     */
    $_formatDatetime: function (date, time, overwriteSecond) {
      let timeMoment = moment(time, VALID_TIME_FORMATS)
      if (timeMoment.isValid()) {
        overwriteSecond != null && timeMoment.second(overwriteSecond)
      } else {
        timeMoment = null
      }

      if (date && timeMoment) {
        return `${date}T${timeMoment.format(TIME_FORMAT_SECOND)}`
      } else if (date) {
        return date
      } else if (timeMoment) {
        return timeMoment.format(TIME_FORMAT_SECOND)
      } else {
        return null
      }
    },

    /**
     * アクティブ期間を更新する
     * @param {String} from 開始日時
     * @param {String} to 終了日時
     */
    $_updateActivePeriod: function (from, to) {
      for (const period of this.periods) {
        if (period.from === from && period.to === to) {
          this.activePeriod = period.value
          break
        }
        if (period.value === PERIOD_VALUE_CUSTOM) {
          this.activePeriod = PERIOD_VALUE_CUSTOM
        }
      }
    },

    /**
     * 開始日時変更イベント発行
     * @param {String} from 変更後開始日時
     */
    $_emitFromChangedEvent: function (from) {
      /**
       * 開始日時変更イベント
       * @param {String} from 変更後開始日時
       */
      this.$emit('from-changed', from)
    },

    /**
     * 終了日時変更イベント発行
     * @param {String} to 変更後開始日時
     */
    $_emitToChangedEvent: function (to) {
      /**
       * 開始日時変更イベント
       * @param {String} to 変更後開始日時
       */
      this.$emit('to-changed', to)
    },

    /**
     * ユーザー検索条件変更時処理
     * @param {String} keyword 検索キーワード
     */
    $_onKeywordChanged: async function (keyword) {
      this.keyword = keyword
      if (this.keyword) {
        await this.$_updateUser()
      } else {
        // 選択を解除する
        this.$_emitUserChangedEvent([])
      }
    },

    /**
     * ユーザー検索項目変更時処理
     * @param {Array.<String>} value 選択された検索項目
     */
    $_onKeywordTargetChanged: async function (value) {
      this.$store.dispatch('userKeywordTarget/setKeywordTargets', value)
      if (this.keyword) {
        await this.$_updateUser()
      }
    },

    /**
     * ユーザーデータを更新
     */
    $_updateUser: async function () {
      const url = RestApiClient.createApiUrl('user')
      url.searchParams.append('keyword', `${this.keyword}`)
      if (this.keywordTargets && this.keywordTargets.length) {
        url.searchParams.append('keyword_target', this.keywordTargets)
      }
      try {
        const json = await RestApiClient.executeGetApi(url)
        const value = json.data.map((u) => ({
          type: 'user',
          id: u.id,
          name: u.user_name,
        }))
        this.$_emitUserChangedEvent(value)
      } catch (error) {
        this.handleApiError(error)
        return
      }
    },

    /**
     * ユーザー選択を表示
     */
    $_showUserSelector: function () {
      this.$refs['user-selector'].showModal()
    },

    /**
     * ユーザー選択変更時処理
     * @param {Array.<Object>} users 選択されたユーザー
     */
    $_emitUserChangedEvent: function (users) {
      /**
       * ユーザー選択変更イベント
       * @param {Array.<Object>} users 選択されたユーザー
       */
      this.$emit('users-changed', users)
    },

    /**
     * ビル変更時処理
     * @param {Number} id 選択されているビルID
     */
    $_onIndoorBuildingSelected: function (id) {
      if (!id) {
        // ビル指定なし → 選択解除
        this.$emit('indoor-buildings-changed')
        return
      }

      for (const indoorBuilding of this.allIndoorBuildings) {
        if (indoorBuilding.id === id) {
          /**
           * ビル選択変更イベント
           * @param {Array.<Object>} buildings 選択されているビル
           */
          this.$emit('indoor-buildings-changed', [indoorBuilding])
          break
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../assets/scss/parts/tool-bar';

.analyze-params-selector {
  border-bottom: none;
  height: 100%;
  padding: 24px 16px 16px 16px;
}

.form-container {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 16px;
}

.form-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  row-gap: 8px;
}

.label {
  color: $text-color;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1;
  padding: 0;
  margin-bottom: 0;
}

.search-period-options {
  width: 100%;

  ::v-deep .btn-light {
    background-color: #f4f5f6 !important;
    font-size: 0.8em !important;

    &:hover {
      background-color: #e0e1e2 !important;
    }

    &.active {
      color: #ffffff !important;
      background-color: $entity-color-green !important;
      box-shadow: none;
    }
  }
}

.target-label-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 8px;

  .target-caption {
    color: $text-color;
    font-size: 0.875rem;
    line-height: 1;

    &.error {
      color: $text-color-error;
    }
  }
}

.target-users-selector-wrapper {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr max-content;
  align-items: flex-start;
  column-gap: 8px;

  .user-list-button {
    width: 6.75rem;
    background-color: $entity-color-green;
    white-space: nowrap;
  }
}

.user-selector {
  ::v-deep .selected-user-input {
    display: none;
  }
}
</style>
